import type { LocationQuery, LocationQueryValue } from 'vue-router';
import {
  useMarketingTrackingStore,
  type AptAttribution,
  type FacebookClickConversion,
  type GoogleClickConversion,
  type MarketingUtm,
  type TiktokClickConversion,
} from '~/stores/apostatudo/marketing-tracking';

const normalizeQueryValue = (
  value?: LocationQueryValue | LocationQueryValue[],
): string | undefined => {
  if (Array.isArray(value)) {
    const [first] = value;

    return first == null || first === '' ? undefined : String(first);
  }

  if (value == null || value === '') {
    return undefined;
  }

  return String(value);
};

const hasAnyValue = (values: Record<string, string | undefined>) => {
  return Object.values(values).some((value) => value !== undefined);
};

export const useMarketingTracking = () => {
  const marketingTrackingStore = useMarketingTrackingStore();

  const persistFromQuery = (query: LocationQuery) => {
    const utmPayload: MarketingUtm = {
      id: normalizeQueryValue(query.utm_id),
      term: normalizeQueryValue(query.utm_term),
      medium: normalizeQueryValue(query.utm_medium),
      source: normalizeQueryValue(query.utm_source),
      content: normalizeQueryValue(query.utm_content),
      campaign: normalizeQueryValue(query.utm_campaign),
    };

    if (hasAnyValue(utmPayload)) {
      marketingTrackingStore.setUtm(utmPayload);
    }

    const googlePayload: GoogleClickConversion = {
      gclid: normalizeQueryValue(query.gclid),
      gbraid: normalizeQueryValue(query.gbraid),
      wbraid: normalizeQueryValue(query.wbraid),
    };

    if (hasAnyValue(googlePayload)) {
      marketingTrackingStore.setGoogle(googlePayload);
    }

    const facebookPayload: FacebookClickConversion = {
      fbclid: normalizeQueryValue(query.fbclid),
    };

    if (hasAnyValue(facebookPayload)) {
      marketingTrackingStore.setFacebook(facebookPayload);
    }

    const tiktokPayload: TiktokClickConversion = {
      ttclid: normalizeQueryValue(query.ttclid),
    };

    if (hasAnyValue(tiktokPayload)) {
      marketingTrackingStore.setTiktok(tiktokPayload);
    }

    const lowerQuery = Object.fromEntries(
      Object.entries(query).map(([k, v]) => [k.toLowerCase(), v]),
    );

    const aptPayload: AptAttribution = {
      channel: normalizeQueryValue(lowerQuery.apt_channel),
      content: normalizeQueryValue(lowerQuery.apt_content),
      extra: normalizeQueryValue(lowerQuery.apt_extra),
    };

    if (hasAnyValue(aptPayload)) {
      marketingTrackingStore.setApt(aptPayload);
    }
  };

  const watchCookies = () => {
    const fbcCookie = useCookie('_fbc', { watch: true });
    const fbpCookie = useCookie('_fbp', { watch: true });

    const persistCookies = () => {
      const facebookCookiePayload: FacebookClickConversion = {
        fbc: fbcCookie.value ?? undefined,
        fbp: fbpCookie.value ?? undefined,
      };

      if (hasAnyValue(facebookCookiePayload)) {
        marketingTrackingStore.setFacebook(facebookCookiePayload);
      }
    };

    watch(fbcCookie, persistCookies, { immediate: true });
    watch(fbpCookie, persistCookies, { immediate: true });
  };

  return {
    persistFromQuery,
    watchCookies,
  };
};
